import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import API from "@/lib/api";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, BookOpen, Users, CheckCircle, Clock, Trash2 } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

const statusColors = {
  upcoming: "bg-[#EFF6FF] text-[#1E40AF] border-[#3B82F6]",
  in_progress: "bg-[#FFFBEB] text-[#92400E] border-[#F59E0B]",
  completed: "bg-[#ECFDF5] text-[#065F46] border-[#10B981]",
  enrolled: "bg-[#EFF6FF] text-[#1E40AF] border-[#3B82F6]",
};

export default function TrainingPage() {
  const { user } = useAuth();
  const [programs, setPrograms] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialog, setCreateDialog] = useState(false);
  const [enrollDialog, setEnrollDialog] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", trainer: "", start_date: "", end_date: "", status: "upcoming", max_participants: "" });
  const [enrollEmpId, setEnrollEmpId] = useState("");

  const isManager = ["admin", "hr_manager"].includes(user?.role);

  useEffect(() => {
    API.get("/training").then(({ data }) => setPrograms(data)).catch(console.error).finally(() => setLoading(false));
    if (isManager) API.get("/employees").then(({ data }) => setEmployees(data)).catch(console.error);
  }, [isManager]);

  const createProgram = async (e) => {
    e.preventDefault();
    try {
      await API.post("/training", { ...form, max_participants: parseInt(form.max_participants) || 0 });
      toast.success("Training program created");
      setCreateDialog(false);
      setForm({ title: "", description: "", trainer: "", start_date: "", end_date: "", status: "upcoming", max_participants: "" });
      API.get("/training").then(({ data }) => setPrograms(data));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const deleteProgram = async (pid) => {
    if (!window.confirm("Delete this training program?")) return;
    await API.delete(`/training/${pid}`);
    toast.success("Deleted");
    API.get("/training").then(({ data }) => setPrograms(data));
  };

  const viewEnrollments = async (prog) => {
    setSelectedProgram(prog);
    const { data } = await API.get(`/training/${prog.id}/enrollments`);
    setEnrollments(data);
    setEnrollDialog(true);
  };

  const enrollEmployee = async () => {
    if (!enrollEmpId) return;
    const emp = employees.find((e) => e.id === enrollEmpId);
    try {
      await API.post(`/training/${selectedProgram.id}/enroll`, { employee_id: enrollEmpId, employee_name: emp?.name || "" });
      toast.success("Employee enrolled");
      const { data } = await API.get(`/training/${selectedProgram.id}/enrollments`);
      setEnrollments(data);
      setEnrollEmpId("");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const markComplete = async (enrollId) => {
    await API.put(`/training/enrollments/${enrollId}`, { status: "completed" });
    toast.success("Marked completed");
    const { data } = await API.get(`/training/${selectedProgram.id}/enrollments`);
    setEnrollments(data);
  };

  return (
    <div className="space-y-6" data-testid="training-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl sm:text-3xl tracking-tight font-medium text-slate-950" style={{ fontFamily: "Outfit, sans-serif" }}>Training</h1>
        {isManager && (
          <button data-testid="create-training-btn" onClick={() => setCreateDialog(true)} className="bg-[#002FA7] hover:bg-[#002480] text-white rounded-sm px-5 py-2.5 font-medium transition-colors text-sm flex items-center gap-2">
            <Plus className="h-4 w-4" /> New Program
          </button>
        )}
      </div>

      {loading ? <div className="text-center py-12 text-sm text-slate-500">Loading...</div> : programs.length === 0 ? (
        <div className="text-center py-12 border border-slate-200 rounded-sm">
          <BookOpen className="h-8 w-8 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500">No training programs yet</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {programs.map((p) => (
            <div key={p.id} className="bg-white border border-slate-200 rounded-sm p-5 flex flex-col" data-testid={`training-card-${p.id}`}>
              <div className="flex items-start justify-between mb-3">
                <h3 className="font-medium text-slate-900 text-sm">{p.title}</h3>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-medium border ${statusColors[p.status] || ""}`}>{p.status?.replace("_", " ")}</span>
              </div>
              <p className="text-xs text-slate-500 mb-3 line-clamp-2">{p.description || "No description"}</p>
              <div className="text-xs text-slate-500 space-y-1 mb-4">
                {p.trainer && <div>Trainer: <span className="text-slate-700">{p.trainer}</span></div>}
                <div>{p.start_date} — {p.end_date || "TBD"}</div>
              </div>
              <div className="mt-auto flex items-center gap-2">
                <button data-testid={`view-enrollments-${p.id}`} onClick={() => viewEnrollments(p)} className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-sm px-3 py-1.5 text-xs font-medium flex items-center gap-1">
                  <Users className="h-3 w-3" /> Enrollments
                </button>
                {isManager && <button onClick={() => deleteProgram(p.id)} className="p-1.5 hover:bg-red-50 rounded-sm text-slate-400 hover:text-red-600 ml-auto"><Trash2 className="h-3.5 w-3.5" /></button>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Create Training Program</DialogTitle><DialogDescription>Set up a new training or development program</DialogDescription></DialogHeader>
          <form onSubmit={createProgram} className="space-y-3 py-2" data-testid="training-form">
            <div><label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">Title</label>
              <input data-testid="training-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 text-sm" placeholder="e.g. Leadership Workshop" /></div>
            <div><label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">Description</label>
              <textarea data-testid="training-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 text-sm resize-none" /></div>
            <div><label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">Trainer</label>
              <input data-testid="training-trainer" value={form.trainer} onChange={(e) => setForm({ ...form, trainer: e.target.value })} className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 text-sm" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">Start Date</label>
                <input data-testid="training-start" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-full text-sm" /></div>
              <div><label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">End Date</label>
                <input data-testid="training-end" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-full text-sm" /></div>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">Status</label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="upcoming">Upcoming</SelectItem><SelectItem value="in_progress">In Progress</SelectItem><SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter className="pt-2">
              <button type="button" onClick={() => setCreateDialog(false)} className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-900 rounded-sm px-4 py-2 text-sm">Cancel</button>
              <button type="submit" data-testid="training-submit" className="bg-[#002FA7] hover:bg-[#002480] text-white rounded-sm px-4 py-2 text-sm">Create</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Enrollments Dialog */}
      <Dialog open={enrollDialog} onOpenChange={setEnrollDialog}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{selectedProgram?.title} — Enrollments</DialogTitle><DialogDescription>Manage participants</DialogDescription></DialogHeader>
          {isManager && (
            <div className="flex items-center gap-2 mb-4">
              <Select value={enrollEmpId} onValueChange={setEnrollEmpId}>
                <SelectTrigger data-testid="enroll-employee-select" className="h-9 flex-1"><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
              </Select>
              <button data-testid="enroll-btn" onClick={enrollEmployee} className="bg-[#002FA7] hover:bg-[#002480] text-white rounded-sm px-3 py-2 text-xs font-medium">Enroll</button>
            </div>
          )}
          {enrollments.length === 0 ? <p className="text-sm text-slate-500 text-center py-4">No enrollments yet</p> : (
            <div className="border border-slate-200 rounded-sm overflow-hidden">
              <Table>
                <TableHeader><TableRow className="bg-slate-50">
                  <TableHead className="px-4 py-2 text-xs uppercase tracking-wider text-slate-500">Employee</TableHead>
                  <TableHead className="px-4 py-2 text-xs uppercase tracking-wider text-slate-500">Status</TableHead>
                  {isManager && <TableHead className="px-4 py-2 text-xs uppercase tracking-wider text-slate-500 text-right">Action</TableHead>}
                </TableRow></TableHeader>
                <TableBody>
                  {enrollments.map((en) => (
                    <TableRow key={en.id} className="border-b border-slate-100">
                      <TableCell className="px-4 py-2 text-sm text-slate-900">{en.employee_name}</TableCell>
                      <TableCell className="px-4 py-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-medium border ${statusColors[en.status] || ""}`}>{en.status}</span>
                      </TableCell>
                      {isManager && (
                        <TableCell className="px-4 py-2 text-right">
                          {en.status !== "completed" && (
                            <button onClick={() => markComplete(en.id)} className="text-xs text-[#002FA7] hover:underline flex items-center gap-1 ml-auto"><CheckCircle className="h-3 w-3" />Complete</button>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
